import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useNotifications } from "@/lib/notifications";
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, LayoutDashboard, BookOpen, BarChart3, Users, Building2, Settings, FileEdit, ClipboardList, BookMarked, Library, Bell, Mail, CheckCircle2, Tags, Activity, Megaphone, Sparkles, BookOpenText } from "lucide-react";
import PrismLogo from "@/components/PrismLogo";

const LINKS = {
  student: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Overview" },
    { to: "/dashboard/exams", icon: BookOpen, label: "Available Exams" },
    { to: "/dashboard/library", icon: BookOpenText, label: "Textbooks" },
    { to: "/dashboard/materials", icon: BookMarked, label: "Study Materials" },
    { to: "/dashboard/results", icon: ClipboardList, label: "My Results" },
    { to: "/dashboard/analytics", icon: BarChart3, label: "Analytics" },
  ],
  teacher: [
    { to: "/teacher", icon: LayoutDashboard, label: "Overview" },
    { to: "/teacher/exams", icon: BookOpen, label: "Exams" },
    { to: "/teacher/exams/new", icon: FileEdit, label: "New Exam" },
    { to: "/teacher/question-bank", icon: Library, label: "Question Bank" },
    { to: "/teacher/ai-textbook", icon: Sparkles, label: "AI Textbook" },
    { to: "/teacher/attempts", icon: ClipboardList, label: "Grading" },
    { to: "/teacher/textbooks", icon: BookOpenText, label: "Textbooks" },
    { to: "/teacher/materials", icon: BookMarked, label: "Materials" },
    { to: "/teacher/classes", icon: Users, label: "Classes" },
  ],
  admin: [
    { to: "/admin", icon: LayoutDashboard, label: "Overview" },
    { to: "/admin/users", icon: Users, label: "Users" },
    { to: "/admin/institutions", icon: Building2, label: "Institutions" },
    { to: "/admin/subjects", icon: Tags, label: "Subjects" },
    { to: "/admin/exams", icon: BookOpen, label: "All Exams" },
    { to: "/admin/question-bank", icon: Library, label: "Question Bank" },
    { to: "/admin/textbooks", icon: BookOpenText, label: "Textbooks" },
    { to: "/admin/materials", icon: BookMarked, label: "Materials" },
    { to: "/admin/announcements", icon: Megaphone, label: "Announcements" },
    { to: "/admin/bulk-email", icon: Mail, label: "Bulk Email" },
    { to: "/admin/activity", icon: Activity, label: "Activity Log" },
    { to: "/admin/settings", icon: Settings, label: "Settings" },
  ],
};

export default function AppShell({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const { items: notifications, unread, markAllRead, markRead } = useNotifications();
  const nav = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const links = LINKS[user?.role] || [];

  const handleLogout = () => {
    logout();
    nav("/");
  };

  const resendVerify = async () => {
    setResendBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent (check your inbox — or the backend logs in dev).");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResendBusy(false);
    }
  };

  const openNotif = async () => {
    setNotifOpen((o) => !o);
    if (!notifOpen && unread > 0) {
      // mark all as read after a moment
      setTimeout(() => markAllRead(), 800);
    }
  };

  const goToNotif = (n) => {
    setNotifOpen(false);
    markRead(n.id);
    if (n.attempt_id && user?.role === "student") {
      nav(`/dashboard/attempts/${n.attempt_id}`);
    }
  };

  const roleTone = {
    admin: "bg-indigo-100 text-indigo-700",
    teacher: "bg-amber-100 text-amber-700",
    student: "bg-emerald-100 text-emerald-700",
  }[user?.role] || "bg-slate-100 text-slate-700";

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-slate-200 flex-col" data-testid="sidebar">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-200">
          <PrismLogo size={36} />
          <span className="font-display text-xl font-extrabold text-slate-900">Prismiq</span>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end
              className={({ isActive }) => `qs-nav-item ${isActive ? "active" : ""}`}
              data-testid={`sidebar-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <l.icon className="w-4 h-4" />
              <span className="text-sm">{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-indigo-600 text-white grid place-items-center font-semibold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
              <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mt-0.5 ${roleTone}`}>
                {user?.role}
              </span>
            </div>
            <button onClick={handleLogout} className="text-slate-500 hover:text-slate-800" title="Log out" data-testid="sidebar-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur px-4 sm:px-8 flex items-center justify-between sticky top-0 z-30" data-testid="app-header">
          <div>
            <h1 className="font-display text-lg sm:text-xl font-bold text-slate-900" data-testid="page-title">{title}</h1>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            <span className={`hidden sm:inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${roleTone}`}>
              {user?.role}
            </span>
            <div className="relative">
              <button
                onClick={openNotif}
                className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition"
                data-testid="notif-bell"
                aria-label="Notifications"
              >
                <Bell className="w-4 h-4" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full grid place-items-center" data-testid="notif-badge">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-auto bg-white rounded-2xl border border-slate-200 shadow-xl z-40" data-testid="notif-panel">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    {notifications.length > 0 && (
                      <button onClick={markAllRead} className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="notif-markall">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {notifications.length === 0 && (
                      <div className="p-8 text-center text-sm text-slate-500" data-testid="notif-empty">
                        You&rsquo;re all caught up.
                      </div>
                    )}
                    {notifications.slice(0, 20).map((n) => (
                      <button
                        key={n.id}
                        onClick={() => goToNotif(n)}
                        className={`w-full text-left p-4 hover:bg-slate-50 transition ${n.read ? "" : "bg-indigo-50/40"}`}
                        data-testid={`notif-item-${n.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                            <CheckCircle2 className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-slate-800">{n.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                            <p className="text-[10px] text-slate-400 mt-1">
                              {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                            </p>
                          </div>
                          {!n.read && <span className="w-2 h-2 rounded-full bg-indigo-500 mt-2 shrink-0" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button onClick={handleLogout} className="btn-ghost text-sm" data-testid="header-logout">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>
        {user && !user.email_verified && (
          <div className="px-4 sm:px-8 py-3 bg-amber-50 border-b border-amber-100 flex flex-wrap items-center justify-between gap-3" data-testid="verify-banner">
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <Mail className="w-4 h-4" />
              <span>
                Please verify your email <b>{user.email}</b> to unlock full account security.
              </span>
            </div>
            <button
              onClick={resendVerify}
              disabled={resendBusy}
              className="text-xs font-semibold text-amber-800 hover:underline disabled:opacity-60"
              data-testid="resend-verify"
            >
              {resendBusy ? "Sending…" : "Resend verification email"}
            </button>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-8 overflow-auto" data-testid="app-main">
          {children}
        </main>
      </div>
    </div>
  );
}
